import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, User as UserIcon, KeyRound, Eye, EyeOff } from "lucide-react";

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const emptyForm = { username: "", password: "", name: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError("");
    setShowPw(false);
    setDialogOpen(true);
  };

  const save = async () => {
    setFormError("");
    const username = form.username.trim().toLowerCase();
    if (username.length < 3) {
      setFormError("Username must be at least 3 characters.");
      return;
    }
    if (form.password.length < 4) {
      setFormError("Password must be at least 4 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users", {
        username,
        password: form.password,
        name: form.name.trim(),
      });
      toast.success(`User @${username} created`);
      setDialogOpen(false);
      load();
    } catch (e) {
      const msg = formatDetail(e?.response?.data?.detail) || "Failed to create user";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success(`Removed @${confirmDelete.username}`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail) || "Failed to delete user");
      setConfirmDelete(null);
    }
  };

  return (
    <div>
      <PageHeader
        testid="users-header"
        title="Users"
        subtitle="Anyone with an account here can sign in and manage the shop."
        actions={
          <Button
            data-testid="add-user-btn"
            onClick={openCreate}
            className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium"
          >
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        }
      />

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">
          Loading users…
        </Card>
      ) : users.length === 0 ? (
        <EmptyState
          testid="users-empty"
          image="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="No users yet"
          description="Add teammates so they can help you run the shop."
          action={
            <Button
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="empty-add-user"
            >
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
          {users.map((u) => {
            const isMe = me?.id === u.id;
            return (
              <Card
                key={u.id}
                className="rounded-2xl border-warm shadow-card bg-white p-5 flex items-start gap-4"
                data-testid={`user-card-${u.username}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#F4D35E]/30 border border-[#E5A82E]/40 flex items-center justify-center shrink-0">
                  <UserIcon className="w-5 h-5 text-[#1A1A1A]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading text-lg font-semibold text-[#2B2D42] truncate">
                      {u.name || u.username}
                    </div>
                    {u.is_seed && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#8B6914] bg-[#FCF5E8] border border-[#EBD9A8] rounded px-1.5 py-0.5"
                        data-testid={`user-seed-badge-${u.username}`}
                      >
                        <ShieldCheck className="w-3 h-3" /> primary
                      </span>
                    )}
                    {isMe && (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-widest font-bold text-[#2F6B4F] bg-[#EAF4F0] border border-[#B5D9C7] rounded px-1.5 py-0.5">
                        you
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-mono text-slate-muted mt-0.5">@{u.username}</div>
                  {u.created_at && (
                    <div className="text-xs text-slate-muted mt-2">
                      Added {new Date(u.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                {!u.is_seed && !isMe && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDelete(u)}
                    data-testid={`delete-user-${u.username}`}
                    className="rounded-lg text-slate-muted hover:text-[#E07A5F] hover:bg-[#F9EDEA]"
                    aria-label={`Remove ${u.username}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add-user dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Add User</DialogTitle>
            <DialogDescription>
              Everyone here has full access to the shop system.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Username *</Label>
              <Input
                data-testid="new-user-username-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. mike"
                autoComplete="off"
                className="mt-1.5 rounded-lg border-2 border-warm font-mono"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Full name</Label>
              <Input
                data-testid="new-user-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Mike Rodriguez"
                autoComplete="off"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Password *</Label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-muted" />
                <Input
                  data-testid="new-user-password-input"
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 4 characters"
                  autoComplete="new-password"
                  className="mt-1.5 pl-9 pr-10 rounded-lg border-2 border-warm"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-muted hover:text-[#2B2D42]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="new-user-toggle-password"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-xs text-slate-muted mt-1.5">
                Share the password with the teammate — they can sign in immediately.
              </div>
            </div>
            {formError && (
              <div
                className="text-sm text-[#8A2E2E] bg-[#F9EDEA] border border-[#E7C2BB] rounded-lg px-3 py-2"
                data-testid="user-form-error"
              >
                {formError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-xl border-2 border-warm"
              data-testid="cancel-user-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={submitting}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl disabled:opacity-70"
              data-testid="save-user-btn"
            >
              {submitting ? "Adding…" : "Add User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Remove @{confirmDelete?.username}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They'll be signed out immediately and won't be able to log back in. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-[#E07A5F] hover:bg-[#C86A4F] text-white rounded-xl"
              data-testid="confirm-delete-user"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
